import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { AlgorithmSettings, ALGORITHM_SETTING_CONFIGS, SettingFieldConfig } from '../../../interfaces/algorithm-settings';

@Component({
  selector: 'app-settings',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatButtonModule, MatFormField, MatLabel, MatInputModule, MatSlideToggleModule],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss'
})
export class SettingsComponent {
  settingsForm: FormGroup;
  settingConfigs = ALGORITHM_SETTING_CONFIGS;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<SettingsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AlgorithmSettings
  ) {
    this.settingsForm = this.createFormWithValidation(data);
  }

  private createFormWithValidation(data: AlgorithmSettings): FormGroup {
    const formGroup: any = {};

    for (const config of this.settingConfigs) {
      const value = data[config.key];
      const validators = this.getValidators(config);

      if (config.type === 'boolean') {
        // Convert string to boolean for form control, will convert back to string on save
        formGroup[config.key] = [value === 'true', validators];
      } else if (config.type === 'decimal') {
        // Normalize comma to dot so number inputs display correctly
        const normalized = typeof value === 'string' ? value.replace(',', '.') : value;
        formGroup[config.key] = [normalized, validators];
      } else {
        formGroup[config.key] = [value, validators];
      }
    }

    return this.fb.group(formGroup);
  }

  private getValidators(config: SettingFieldConfig) {
    const validators = [Validators.required];

    if (config.type === 'integer') {
      validators.push(Validators.pattern(/^\d+$/));
      if (config.min !== undefined) {
        validators.push(Validators.min(config.min));
      }
      if (config.max !== undefined) {
        validators.push(Validators.max(config.max));
      }
    } else if (config.type === 'decimal') {
      validators.push(Validators.pattern(/^\d+([.,]\d+)?$/));
      if (config.min !== undefined) {
        validators.push(Validators.min(config.min));
      }
    }

    return validators;
  }

  getFieldConfig(key: string): SettingFieldConfig | undefined {
    return this.settingConfigs.find(config => config.key === key);
  }

  save() {
    if (this.settingsForm.valid) {
      // Convert form values back to string format for JSON output
      const formValue = this.settingsForm.value;
      const result: AlgorithmSettings = {} as AlgorithmSettings;

      for (const config of this.settingConfigs) {
        const value = formValue[config.key];
        if (config.type === 'boolean') {
          result[config.key] = value.toString();
        } else if (config.type === 'decimal') {
          // Use comma as decimal separator for NL culture algorithm
          result[config.key] = value.toString().replace('.', ',');
        } else {
          result[config.key] = value.toString();
        }
      }

      this.dialogRef.close(result);
    }
  }

  cancel() {
    // Close the dialog without saving
    this.dialogRef.close();
  }
}
